import { Amplify } from "@aws-amplify/core";
import { Storage } from "@aws-amplify/storage";
import { DataStore, SortDirection } from "@aws-amplify/datastore";
import { useCallback, useEffect, useRef, useState } from "react";
import { withAuthenticator } from "@aws-amplify/ui-react";
import { v4 } from "uuid";

import awsconfig from "../aws-exports";
import {
  Button,
  Container,
  Dimmer,
  Divider,
  Header,
  Loader,
  Message,
  Segment,
} from "semantic-ui-react";
import { Viewer } from "../models";
import { UploadsList } from "../components/UploadsList";
import { BundleInfo } from "../components/BundleInfo";
import { ActionModal } from "../components/ActionModal";
import {
  generateApplicationCfg,
  generateManifestFile,
  getBundleInfo,
} from "../utils/kanziUtils";

Amplify.configure(awsconfig);

//configure Storage with config for unauthenticated storage access
const PAGE_SIZE = 1000;
Storage.configure({ level: "public" });

function Upload({ signOut, user }) {
  const fileInput = useRef();
  const [bundleInfo, setBundleInfo] = useState({
    name: "",
    fileNames: [],
    nameSource: "",
  });
  const [deleteError, setDeleteError] = useState("");
  const [bundleError, setBundleError] = useState("");
  const [uploads, setUploads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState({
    id: "",
    name: "",
  });
  const [customNameValue, setCustomNameValue] = useState("");

  const fetchViewers = useCallback(async () => {
    const author = user?.attributes?.email;
    const viewers = await DataStore.query(
      Viewer,
      (v) => v.author.contains(author),
      {
        sort: (viewer) => viewer.createdAt(SortDirection.DESCENDING),
      },
    );

    setUploads(viewers);
  }, [user?.attributes?.email]);

  useEffect(() => {
    fetchViewers();
    const subscription = DataStore.observe(Viewer).subscribe(() =>
      fetchViewers(),
    );
    return () => subscription.unsubscribe();
  }, [fetchViewers]);

  const handleChange = async () => {
    setBundleError("");
    const inputValue = fileInput?.current?.files;

    const selectedFiles = Object.values(inputValue);

    const bundleInfo = await getBundleInfo(selectedFiles, setBundleError);

    setBundleInfo(bundleInfo);
  };

  const handleUploadClick = async (event) => {
    event.preventDefault();

    setIsLoading(true);
    let newArr = fileInput?.current?.files;
    const folderName = `bundles/${v4()}`;

    // auto generate application.cfg file in case it is missing from the user selected files
    const autogeneratedApplicationCfg = await generateApplicationCfg(newArr);
    if (autogeneratedApplicationCfg) {
      newArr = [...newArr, autogeneratedApplicationCfg];
    }

    // auto generate manifest.txt file in case it is missing from the user selected files
    const autogeneratedManifest = await generateManifestFile(newArr);
    if (autogeneratedManifest) {
      newArr = [...newArr, autogeneratedManifest];
    }

    // Upload the user selected files
    for (let i = 0; i < newArr.length; i++) {
      await handleUpload(folderName, newArr[i]);
    }

    await DataStore.save(
      new Viewer({
        bundle_s3_address: `${folderName}`,
        bundle_version: bundleInfo?.bundleVersion,
        player_version: bundleInfo?.playerVersion,
        name: customNameValue || bundleInfo.name,
        author: user?.attributes?.email,
      }),
    );

    fileInput.current.value = null;
    setIsLoading(false);
    setCustomNameValue("");
    setBundleInfo({ name: "", fileNames: [], nameSource: "" });
  };

  const handleDeleteViewer = async (bundle) => {
    setIsLoading(true);

    const { bundle_s3_address } = await DataStore.query(Viewer, bundle.id);
    const files = await Storage.list(bundle_s3_address, {
      pageSize: PAGE_SIZE,
    }).catch((err) => {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteError("Unable to list bundle files!");
      return;
    });
    if ((files?.results || []).length === 0) {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteError("Unable to find bundle files!");
      return;
    }

    // Delete bundle s3 files
    for (let i = 0; i < files.results.length; i++) {
      const res = await Storage.remove(`${files.results[i].key}`);
      const { httpStatusCode } = res.$metadata;
      if (httpStatusCode !== 204) {
        setIsLoading(false);
        setIsDeleteModalOpen(false);
        setDeleteError(`Unable to delete bundle file: ${files.results[i].key}`);
        return;
      }
    }

    // Delete bundle Viewer data
    await DataStore.delete(Viewer, bundle.id);
    setIsLoading(false);
    setIsDeleteModalOpen(false);
  };

  const handleUpload = async (folder, file) => {
    const filePath = `${folder}/${file.name}`;
    console.log(`uploading => ${filePath}`);
    let savedFile = await Storage.put(filePath, file, {
      cacheControl: "max-age=864000", // browser cache valid for 10 days
      progressCallback: (x) => {
        console.log(x);
      },
    }).catch((err) => {
      console.log(err);
    });
    console.log("saved file =>", savedFile);
  };

  return (
    <Container>
      {/* <>
        <h1>Hello {user?.attributes?.email}</h1>
        <button onClick={signOut}>Sign out</button>
      </> */}
      <div className="uploadWrapper">
        <Header as="h2">Upload Kanzi bundle files</Header>
        <form className="upload-steps" onSubmit={handleUploadClick}>
          <Segment>
            <label>
              <span className="labelText">Select Files:</span>

              <input
                type="file"
                multiple
                ref={fileInput}
                onChange={handleChange}
              />
            </label>
          </Segment>
          {bundleInfo?.fileNames.length > 0 && !bundleError && (
            <>
              <Divider />
              <BundleInfo
                bundleInfo={bundleInfo}
                customNameValue={customNameValue}
                setCustomNameValue={setCustomNameValue}
              />
              <Button
                primary
                type="submit"
                content="Upload"
                disabled={!!bundleError}
              />
            </>
          )}
          {bundleError && (
            <div>
              <Message compact warning>
                {bundleError}
              </Message>
            </div>
          )}
        </form>
        <Divider />

        {deleteError ? (
          <Container>
            <Segment
              style={{
                // display: "flex",
                // justifyContent: "center",
                marginTop: 15,
              }}
            >
              <Header as="h2">Something went wrong...</Header>
              <Message
                icon="times circle outline"
                header="Failed to delete the bundle"
                content={deleteError}
              />
            </Segment>
          </Container>
        ) : (
          <></>
        )}

        {isLoading ? (
          <Dimmer active inverted>
            <Loader inverted>Loading</Loader>
          </Dimmer>
        ) : (
          <Segment>
            <UploadsList
              uploads={uploads}
              handleDeleteViewer={(id, name) => {
                setIsDeleteModalOpen(true);
                setSelectedForDeletion({ id, name });
              }}
            />
          </Segment>
        )}
      </div>
      <ActionModal
        isOpen={isDeleteModalOpen}
        openHandler={() => setIsDeleteModalOpen(true)}
        closeHandler={() => setIsDeleteModalOpen(false)}
        actionHandler={() => handleDeleteViewer(selectedForDeletion)}
        title="Delete Bundle"
        content={`Are you sure you want to delete bundle "${selectedForDeletion.name}"?`}
        cancelText="Cancel"
        actionText="Delete"
      />
    </Container>
  );
}

export default withAuthenticator(Upload);
